import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { UserService } from '../../service/user.service';
import { LoaderService } from '../../service/loader.service';
import { Store } from '@ngrx/store';
import { loginFailure } from '../../store/counter.actions';
import { LoginState } from '../../store/counter.state';
import { nanoid } from 'nanoid';
import { messaging } from '../../../../firebase-config';
import { getOrRefreshFcmToken } from '../../service/fcm-service-worker';
import { ChatService } from '../../service/chat.service';
import { AuthService } from '../../service/auth-service';
import { NotificationSoundService } from '../../service/notification-sound.service';
import { ThemeService } from '../../service/theme.service';
import { UpdateButtonComponent } from '../update-button/update-button.component';

export interface UserData {
  department: string;
  userName: string;
  userId: string;
  userEmail: string;
  profileImg: string;
  modules: any;
  subModules?: any;
  token: string;
  isHead?: boolean;
}

export interface JwtResponse {
  data?: UserData;
  token?: string;
  message: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  imports: [ReactiveFormsModule, FormsModule, UpdateButtonComponent]
})
export class LoginComponent implements OnInit, OnDestroy {
  public step: 'credentials' | 'otp' = 'credentials';
  public otpToken: string = '';
  public maskedEmail: string = '';
  public otpValue: string = '';
  public resendCountdown: number = 0;
  private resendTimer: any = null;
  public canResend: boolean = false;

  public errorMessage: string = '';
  public fcmToken: string = '';
  public isLoggingIn: boolean = false;
  public showPassword: boolean = false;
  public passwordNotEmpty: boolean = false;
  public isLoading: boolean = false;

  get version(): string {
    return localStorage.getItem('version') || 'V3';
  }

  public readonly loginForm: FormGroup = new FormGroup({
    username: new FormControl(''),
    password: new FormControl(''),
  });

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly loaderService: LoaderService,
    private readonly store: Store<{ login: LoginState }>,
    private readonly chatService: ChatService,
    private readonly authService: AuthService,
    private readonly soundService: NotificationSoundService,
    private readonly themeService: ThemeService
  ) { }

  public ngOnInit(): void {
    localStorage.setItem('version', 'V3');
    this.themeService.initTheme();
    this.generateToken();
    this.getBrowserUniqueId();
  }

  public ngOnDestroy(): void {
    this.clearResendCountdown();
  }

  public async onLogin(): Promise<void> {
    if (this.isLoggingIn) return;
    const username = this.loginForm.value.username?.trim();
    const password = this.loginForm.value.password;
    if (!username || !password) {
      this.errorMessage = 'Please enter both username and password';
      return;
    }

    this.isLoggingIn = true;
    this.errorMessage = '';

    const browserUniqueId = this.getBrowserUniqueId();
    const tokenRequest = this.generateToken();
    const fcmToken = await this.resolveFcmToken(tokenRequest);

    this.userService.login({ username, password, fcmToken, browserUniqueId }).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data?.otpToken) {
          this.otpToken = data.otpToken;
          this.maskedEmail = data.email || '';
          this.step = 'otp';
          this.otpValue = '';
          this.errorMessage = '';
          this.startResendCountdown(60);
        } else if (data?.token) {
          this.handleLoginSuccess(res);
          this.syncLateFcmToken(tokenRequest, fcmToken, browserUniqueId);
          this.redirectAfterLogin();
        } else {
          this.handleLoginError('Unable to send OTP, please try again!');
        }
        this.isLoggingIn = false;
      },
      error: (err: any) => {
        const message = LoginComponent.serverErrorText(err, 'Invalid username or password');
        this.handleLoginError(message);
        this.isLoggingIn = false;
      }
    });
  }

  public onOtpInput(event: any): void {
    const input = event?.target as HTMLInputElement;
    const rawValue = input ? input.value : '';
    const cleanValue = rawValue.replace(/\D/g, '').slice(0, 6);
    const cursorPos = input?.selectionStart;

    this.otpValue = cleanValue;
    if (input) {
      if (input.value !== cleanValue) {
        input.value = cleanValue;
        if (cursorPos !== null && cursorPos !== undefined) {
          const newPos = Math.min(cursorPos, cleanValue.length);
          input.setSelectionRange(newPos, newPos);
        }
      }
    }
    this.errorMessage = '';

    if (cleanValue.length === 6 && !this.isLoggingIn) {
      this.onVerifyOtp();
    }
  }

  public onOtpKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const input = event.target as HTMLInputElement;
      if (input) {
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        // If all text is selected, cleanly delete it
        if (start === 0 && end === input.value.length && input.value.length > 0) {
          input.value = '';
          this.otpValue = '';
          this.errorMessage = '';
          event.preventDefault();
          return;
        }
        // If single character remaining:
        // Backspace deletes when cursor is after the character (start > 0)
        // Delete deletes when cursor is before the character (start === 0)
        if (input.value.length === 1) {
          if ((event.key === 'Backspace' && start > 0) || (event.key === 'Delete' && start === 0)) {
            input.value = '';
            this.otpValue = '';
            this.errorMessage = '';
            event.preventDefault();
            return;
          }
        }
      }
    }
  }

  public onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const cleanDigits = pastedData.replace(/\D/g, '');
    if (!cleanDigits) return;

    const input = event.target as HTMLInputElement;
    let newValue = cleanDigits.slice(0, 6);

    // If input already has some digits and pasted text is partial (< 6 digits), splice at cursor
    if (input && input.value && cleanDigits.length < 6) {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? input.value.length;
      const combined = (input.value.slice(0, start) + cleanDigits + input.value.slice(end)).replace(/\D/g, '').slice(0, 6);
      newValue = combined;
    }

    this.otpValue = newValue;
    if (input) {
      input.value = newValue;
      const pos = Math.min(newValue.length, 6);
      input.setSelectionRange(pos, pos);
    }
    this.errorMessage = '';

    if (newValue.length === 6 && !this.isLoggingIn) {
      this.onVerifyOtp();
    }
  }

  public async onVerifyOtp(): Promise<void> {
    if (this.isLoggingIn) return;
    if (!this.otpValue || this.otpValue.length !== 6) {
      this.errorMessage = 'Please enter a valid 6-digit OTP';
      return;
    }
    if (!this.otpToken) {
      this.errorMessage = 'Login session expired, please log in again!';
      this.step = 'credentials';
      return;
    }

    this.isLoggingIn = true;
    this.errorMessage = '';

    const browserUniqueId = this.getBrowserUniqueId();
    const tokenRequest = this.generateToken();
    const fcmToken = await this.resolveFcmToken(tokenRequest);

    const payload = {
      otpToken: this.otpToken,
      otp: this.otpValue,
      fcmToken,
      browserUniqueId
    };

    this.userService.verifyLoginOtp(payload).subscribe({
      next: (jwt_resp: any) => {
        if (!jwt_resp?.data) {
          this.errorMessage = 'Verification failed. Please try again.';
          this.isLoggingIn = false;
          return;
        }

        this.handleLoginSuccess(jwt_resp);
        this.syncLateFcmToken(tokenRequest, fcmToken, browserUniqueId);
        this.redirectAfterLogin();
        this.isLoggingIn = false;
      },
      error: (err: any) => {
        const msg = LoginComponent.serverErrorText(err, 'Verification failed. Please try again.');
        this.errorMessage = msg;

        const lowerMsg = msg.toLowerCase();
        if (lowerMsg.includes('session expired') || (err?.status === 401 && lowerMsg.includes('log in again'))) {
          this.step = 'credentials';
          this.otpToken = '';
          this.otpValue = '';
          this.clearResendCountdown();
        } else if (lowerMsg.includes('too many wrong attempts') || lowerMsg.includes('expired')) {
          this.otpValue = '';
          this.clearResendCountdown();
          this.canResend = true;
        }

        this.isLoggingIn = false;
      }
    });
  }

  public onResendOtp(): void {
    if (this.isLoggingIn || (this.resendCountdown > 0 && !this.canResend)) return;
    if (!this.otpToken) {
      this.step = 'credentials';
      this.errorMessage = 'Login session expired, please log in again!';
      return;
    }

    this.isLoggingIn = true;
    this.errorMessage = '';

    this.userService.resendLoginOtp(this.otpToken).subscribe({
      next: (res: any) => {
        if (res?.data?.otpToken) {
          this.otpToken = res.data.otpToken;
        }
        if (res?.data?.email) {
          this.maskedEmail = res.data.email;
        }
        this.otpValue = '';
        this.canResend = false;
        this.startResendCountdown(60);
        this.isLoggingIn = false;
      },
      error: (err: any) => {
        const msg = LoginComponent.serverErrorText(err, 'Failed to resend OTP. Please try again.');
        this.errorMessage = msg;
        if (err?.status === 401 || msg.toLowerCase().includes('session expired')) {
          this.step = 'credentials';
          this.otpToken = '';
          this.otpValue = '';
          this.clearResendCountdown();
        }
        this.isLoggingIn = false;
      }
    });
  }

  public onBackToCredentials(): void {
    this.step = 'credentials';
    this.otpToken = '';
    this.otpValue = '';
    this.errorMessage = '';
    this.clearResendCountdown();
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public onPasswordInputChange(): void {
    const passwordValue = this.loginForm.get('password')?.value;
    this.passwordNotEmpty = passwordValue && passwordValue.length > 0;
  }

  public onInputClick(): void {
    this.errorMessage = '';
  }

  private startResendCountdown(seconds: number = 60): void {
    this.clearResendCountdown();
    this.resendCountdown = seconds;
    this.canResend = false;
    this.resendTimer = setInterval(() => {
      if (this.resendCountdown > 0) {
        this.resendCountdown--;
      } else {
        this.clearResendCountdown();
        this.canResend = true;
      }
    }, 1000);
  }

  private clearResendCountdown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.resendCountdown = 0;
  }

  private redirectAfterLogin(): void {
    if (localStorage.getItem('mustChangePassword') === 'true') {
      this.router.navigate(['/change-password']);
      return;
    }
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');

      let targetUrl = redirectUrl;
      if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
        try {
          const urlObj = new URL(redirectUrl);
          if (urlObj.hash) {
            targetUrl = urlObj.hash.substring(1);
            if (!targetUrl.startsWith('/')) {
              targetUrl = '/' + targetUrl;
            }
          } else {
            targetUrl = urlObj.pathname + urlObj.search;
          }
        } catch (e) {
          targetUrl = '/home';
        }
      }

      if (targetUrl && targetUrl !== '/' && targetUrl !== '/login' && !targetUrl.startsWith('/login?')) {
        this.router.navigateByUrl(targetUrl);
      } else {
        this.router.navigate(['/home']);
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  private handleLoginSuccess(jwt_resp: JwtResponse): void {
    if (jwt_resp?.data) {
      const data: UserData = jwt_resp.data;

      localStorage.setItem('guestToken', data.token);
      localStorage.setItem('department', data.department);
      localStorage.setItem('username', data.userName);
      localStorage.setItem('guestID', data.userId);
      this.themeService.initTheme();
      localStorage.setItem('email', data.userEmail);
      localStorage.setItem('profileImg', data.profileImg);
      localStorage.setItem('isHead', String(data.isHead === true));
      localStorage.setItem('mustChangePassword', (data as any).mustChangePassword === true ? 'true' : 'false');

      const modulesData = data.modules || (data as any).moduleAccess;
      if (modulesData) {
        const modulesVal = Array.isArray(modulesData) ? modulesData.join(",") : modulesData;
        localStorage.setItem('modules', modulesVal);
      } else {
        localStorage.setItem('modules', "");
      }

      const subModulesData = data.subModules || (data as any).subModuleAccess;
      if (subModulesData) {
        const subModulesVal = typeof subModulesData === 'object' ? JSON.stringify(subModulesData) : subModulesData;
        localStorage.setItem('subModules', subModulesVal);
      } else {
        localStorage.setItem('subModules', "");
      }

      this.chatService.listenNotificationMsg();
      this.authService.setLoginTimestamp();
      this.authService.scheduleTokenRefresh(data.token);
      this.soundService.syncToServiceWorker('active_user_id', data.userId);
      this.soundService.syncAllSettings();
    }
  }

  /**
   * The backend's exception filter nests the real text one level down
   * (err.error.message is itself { message, error, statusCode }), and
   * validation errors arrive as an array. Flatten all of that to a string so
   * the page never shows "[object Object]".
   */
  private static serverErrorText(err: any, fallback: string): string {
    const pick = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) return v.map(pick).filter(Boolean).join(', ') || null;
      if (typeof v === 'object') return pick(v.message) || pick(v.error) || null;
      return null;
    };
    return pick(err?.error?.message) || pick(err?.error) || pick(err?.message) || fallback;
  }

  private handleLoginError(message: string = 'Invalid username or password'): void {
    this.errorMessage = message;
    this.store.dispatch(loginFailure({ errorMessage: this.errorMessage }));
  }

  private getBrowserUniqueId(): string {
    let id = localStorage.getItem('browserUniqueId');
    if (!id) {
      id = nanoid(16);
      localStorage.setItem('browserUniqueId', id);
    }
    return id;
  }

  private async resolveFcmToken(tokenRequest: Promise<string>): Promise<string> {
    let fcmToken = '';
    try {
      console.log('[FCM Diagnostics] resolveFcmToken: Starting generateToken() race with 3000ms timeout...');
      const startTime = Date.now();
      fcmToken = await Promise.race([
        tokenRequest,
        new Promise<string>((resolve) =>
          setTimeout(() => {
            console.warn('[FCM Diagnostics] resolveFcmToken: generateToken() timed out after 3000ms!');
            resolve('');
          }, 3000)
        ),
      ]);
      const elapsed = Date.now() - startTime;
      console.log(`[FCM Diagnostics] resolveFcmToken: generateToken() race finished in ${elapsed}ms. Result:`, fcmToken ? 'TOKEN_OBTAINED' : 'EMPTY_TOKEN');
    } catch (err) {
      console.warn('[FCM Diagnostics] resolveFcmToken: Could not generate FCM token before login:', err);
    }

    if (!fcmToken) {
      const fallbackToken = localStorage.getItem('fcm_token') || this.fcmToken || '';
      console.warn('[FCM Diagnostics] resolveFcmToken: Primary token generation was empty. Fallback token:', fallbackToken ? 'FOUND_IN_STORAGE' : 'NONE');
      fcmToken = fallbackToken;
    }
    return fcmToken;
  }

  private syncLateFcmToken(tokenRequest: Promise<string>, sentToken: string, browserUniqueId: string): void {
    tokenRequest.then((token) => {
      const userId = localStorage.getItem('guestID');
      if (!token || token === sentToken || !userId) return;

      console.log('[FCM Diagnostics] FCM token arrived after login, syncing to backend...');
      this.userService.updateToken({ id: userId, token, browserUniqueId }).subscribe({
        next: () => console.log('[FCM Diagnostics] Late FCM token synced to backend'),
        error: (err) => console.error('[FCM Diagnostics] Failed to sync late FCM token:', err),
      });
    });
  }

  private async generateToken(): Promise<string> {
    console.log('[FCM Diagnostics] generateToken() initiated...');
    const token = await getOrRefreshFcmToken(messaging);
    if (token) {
      this.fcmToken = token;
      console.log('[FCM Diagnostics] generateToken() successfully updated this.fcmToken');
    } else {
      console.warn('[FCM Diagnostics] generateToken() returned empty token');
    }
    return token;
  }
}
